import { z } from 'zod';

export const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  fullName: z.string().min(1, 'Full name is required'),
  orgName: z.string().min(1, 'Organization name is required'),
});

export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token is required'),
});

export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>;

export const googleAuthSchema = z.object({
  idToken: z.string().min(1, 'Google ID token is required'),
});

export type GoogleAuthInput = z.infer<typeof googleAuthSchema>;

export const facebookAuthSchema = z.object({
  accessToken: z.string().min(1, 'Facebook access token is required'),
});

export type FacebookAuthInput = z.infer<typeof facebookAuthSchema>;

export const onboardingSchema = z.object({
  industry: z.enum(['IT_WEB', 'DESIGN', 'CONTENT', 'CONSULTING', 'OTHER']).optional(),
  teamSize: z.enum(['SOLO', 'SMALL', 'MEDIUM']).optional(),
  preferredChannel: z.enum(['ZALO', 'EMAIL', 'BOTH']).optional(),
  defaultCurrency: z.enum(['VND', 'USD', 'BOTH']).optional(),
  showTeacherAssignment: z.boolean().optional(),
  sellsExtras: z.boolean().optional(),
  businessType: z.enum(['UNREGISTERED', 'HOUSEHOLD', 'ENTERPRISE']).optional(),
  // Initial chargeAbsent value for new PER_SESSION enrollments (org-level
  // policy default). Changing it never touches existing enrollments — see
  // POST /enrollments/charge-absent/bulk for that.
  defaultChargeAbsent: z.boolean().optional(),
  // Onboarding v2 (spec 2026-08-23-onboarding-v2-design §1): the wizard asks
  // the excused-absence cap and the invoice due-day directly, so both must
  // survive `POST /auth/onboarding` instead of being settings-screen-only.
  // Same shape as updateOrgSettingsSchema in ./organization.ts — `null` is a
  // real value (no cap / no fixed due day), `undefined` means "not mentioned"
  // and the route's partial-update branch leaves the stored value alone.
  maxExcusedPerMonth: z.number().int().min(0).nullable().optional(),
  // 1–28 so every month has that day; a teacher wanting "the 30th" picks 28.
  invoiceDueDay: z.number().int().min(1).max(28).nullable().optional(),
  // THI-267: COHORT (a trung tâm's shared-roster classes) vs INDIVIDUAL (the
  // historical one-slot-per-student default) — see OrganizationSettings.classMode.
  // Not nullable: there is no "unset" state, the column default already covers it.
  classMode: z.enum(['COHORT', 'INDIVIDUAL']).optional(),
});

export type OnboardingInput = z.infer<typeof onboardingSchema>;

export const inviteMemberSchema = z.object({
  email: z.string().email('Invalid email address'),
  role: z.enum(['ADMIN', 'MEMBER']).default('MEMBER'),
});

export type InviteMemberInput = z.infer<typeof inviteMemberSchema>;

export const acceptInviteSchema = z.object({
  token: z.string().min(1, 'Invitation token is required'),
  fullName: z.string().min(1, 'Full name is required').optional(),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
});

export type AcceptInviteInput = z.infer<typeof acceptInviteSchema>;

export const updateMemberRoleSchema = z.object({
  role: z.enum(['ADMIN', 'MEMBER']),
});

export type UpdateMemberRoleInput = z.infer<typeof updateMemberRoleSchema>;

export const createPlaceholderMemberSchema = z.object({
  name: z.string().min(1, 'Name is required'),
});

export type CreatePlaceholderMemberInput = z.infer<typeof createPlaceholderMemberSchema>;

export const convertMemberSchema = z.object({
  email: z.string().email('Invalid email address'),
});

export type ConvertMemberInput = z.infer<typeof convertMemberSchema>;

export const impersonateExchangeSchema = z.object({
  code: z.string().min(1, 'Thiếu mã phiên'),
});

export type ImpersonateExchangeInput = z.infer<typeof impersonateExchangeSchema>;
